import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getSocietyAdminData,
  getAdminSocietyDetail,
  updateSociety,
  getBlocks,
  getSocietySettings,
  updateSocietySetting,
  resetSocietySetting,
  AdminSocietyDetail,
  AdminSocietyDetailSociety,
  AdminSocietyActivityLog,
  Block,
} from '../services/api';
import {
  HiOfficeBuilding,
  HiPencil,
  HiCheck,
  HiX,
  HiRefresh,
  HiExclamation,
  HiCog,
  HiInformationCircle,
  HiChevronDown,
  HiDocumentText,
} from '../components/Icons';
import SocietyAdminBillCategories from './SocietyAdminBillCategories';
import SocietyAdminInterestConfig from './SocietyAdminInterestConfig';
import './SocietyAdminDetails.css';
import './SocietyAdminInterestConfig.css';

type SocietyFormData = Partial<{
  name: string;
  location: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
  type: string;
}>;

const SOCIETY_TYPES = ['apartment', 'villa', 'township', 'rowhouse', 'other'];

/** Settings managed only by TappyTray Admin — hidden from Society Admin portal. */
const TAPPYTRAY_ADMIN_ONLY_SETTINGS = new Set([
  'other_checkin_notify_roles',
  'other_checkin_notify', // legacy key
]);

/** Known allowed values for settings that should use a dropdown in Society Admin. */
const SETTING_SELECT_OPTIONS: Record<string, Array<{ value: string; label: string }>> = {
  member_approval_by: [
    { value: 'society_admin', label: 'Society Admin' },
    { value: 'flat_owner', label: 'Flat Owner' },
    { value: 'both', label: 'Both' },
  ],
  tenant_approval_by: [
    { value: 'society_admin', label: 'Society Admin' },
    { value: 'flat_owner', label: 'Flat Owner' },
    { value: 'both', label: 'Both' },
  ],
  tenant_can_add_family: [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
  ],
  reconciliation_enabled: [
    { value: 'yes', label: 'Enabled' },
    { value: 'no', label: 'Disabled' },
  ],
  guard_can_see_family_members: [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
  ],
  removal_approval_by: [
    { value: 'society_admin', label: 'Society Admin' },
    { value: 'flat_owner', label: 'Flat Owner' },
    { value: 'self', label: 'Self' },
  ],
};

const SETTING_LABELS: Record<string, string> = {
  member_approval_by: 'Member Approval',
  tenant_approval_by: 'Tenant Approval',
  tenant_can_add_family: 'Tenant Family',
  maintenance_bill_visible_to: 'Bill Visibility',
  maintenance_bill_notify: 'Bill Notifications',
  removal_approval_by: 'Removal Approval',
  reconciliation_enabled: 'Reconciliation Module',
  guard_can_see_family_members: 'Guard Family Contacts',
  statement_parser_id: 'Bank Statement Parser',
};

const formatDate = (dateStr: string) => {
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

const SocietyAdminDetails: React.FC = () => {
  const location = useLocation();
  const adminData = getSocietyAdminData();
  const societyId = adminData?.societyId ?? 0;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState<AdminSocietyDetail | null>(null);
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [settings, setSettings] = useState<Record<string, string>>({});

  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState<SocietyFormData>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const [editingSettingKey, setEditingSettingKey] = useState<string | null>(null);
  const [settingValue, setSettingValue] = useState('');
  const [savingSetting, setSavingSetting] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const [configOpen, setConfigOpen] = useState(false);
  const configWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!configOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!configWrapRef.current?.contains(e.target as Node)) {
        setConfigOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [configOpen]);

  const scrollToSocietySettings = () => {
    setConfigOpen(false);
    document.getElementById('sad-society-settings')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const scrollToBillCategories = () => {
    setConfigOpen(false);
    document.getElementById('sad-bill-categories')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const scrollToInterestConfig = () => {
    setConfigOpen(false);
    document.getElementById('sad-interest-config')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    const raw = location.hash.replace(/^#/, '');
    if (!raw || !data?.society) return;
    const idMap: Record<string, string> = {
      'bill-categories': 'sad-bill-categories',
      'interest-config': 'sad-interest-config',
      'society-settings': 'sad-society-settings',
    };
    const elId = idMap[raw];
    if (!elId) return;
    const t = window.setTimeout(() => {
      document.getElementById(elId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
    return () => window.clearTimeout(t);
  }, [location.hash, location.pathname, data?.society]);

  const fetchAll = useCallback(async () => {
    if (!societyId) {
      setLoading(false);
      setError('No society selected. Please select a society from the menu.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [detailRes, blocksRes, settingsRes] = await Promise.all([
        getAdminSocietyDetail(societyId),
        getBlocks(societyId),
        getSocietySettings(societyId),
      ]);
      setData(detailRes.data);
      setBlocks(blocksRes.data);
      setSettings(settingsRes.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load society details');
    } finally {
      setLoading(false);
    }
  }, [societyId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const startEditing = (society: AdminSocietyDetailSociety) => {
    setFormData({
      name: society.name,
      location: society.location,
      city: society.city,
      state: society.state || '',
      pincode: String(society.pincode || ''),
      country: society.country || '',
      type: society.type || '',
    });
    setSaveError('');
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setFormData({});
    setSaveError('');
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      await updateSociety(societyId, formData);
      setEditing(false);
      await fetchAll();
    } catch (err: any) {
      setSaveError(err.message || 'Failed to update society');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSetting = async (key: string) => {
    setSavingSetting(true);
    setSettingsError('');
    try {
      const res = await updateSocietySetting(societyId, key, settingValue);
      setSettings(res.data);
      setEditingSettingKey(null);
    } catch (err: any) {
      setSettingsError(err.message || 'Failed to update setting');
    } finally {
      setSavingSetting(false);
    }
  };

  const handleResetSetting = async (key: string) => {
    setSavingSetting(true);
    setSettingsError('');
    try {
      const res = await resetSocietySetting(societyId, key);
      setSettings(res.data);
      setEditingSettingKey(null);
    } catch (err: any) {
      setSettingsError(err.message || 'Failed to reset setting');
    } finally {
      setSavingSetting(false);
    }
  };

  const society = data?.society;

  return (
    <div className="sad-page">
      <div className="sad-top-bar">
        <h2 className="sad-page-title">
          <HiOfficeBuilding size={22} /> Society Details
        </h2>
        <div className="sad-top-bar-actions">
          <div className="sad-config-wrap" ref={configWrapRef}>
            <button
              type="button"
              className="sad-btn sad-btn-primary sad-config-trigger"
              onClick={() => setConfigOpen((o) => !o)}
              aria-expanded={configOpen}
              aria-haspopup="menu"
            >
              <HiCog size={18} /> Configuration
              <HiChevronDown size={18} className={configOpen ? 'sad-chevron-open' : ''} />
            </button>
            {configOpen && (
              <div className="sad-config-dropdown" role="menu">
                <button type="button" className="sad-config-item" role="menuitem" onClick={scrollToSocietySettings}>
                  Society Settings
                </button>
                <button type="button" className="sad-config-item" role="menuitem" onClick={scrollToBillCategories}>
                  Bill Categories
                </button>
                <button type="button" className="sad-config-item" role="menuitem" onClick={scrollToInterestConfig}>
                  Interest Config
                </button>
              </div>
            )}
          </div>
          <button className="sad-btn sad-btn-outline" onClick={fetchAll} disabled={loading}>
            <HiRefresh size={16} className={loading ? 'spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="sad-alert sad-alert-error">
          <HiExclamation size={18} /> {error}
        </div>
      )}

      {loading && (
        <div className="sad-loading">
          <HiRefresh size={32} className="spin" />
          <p>Loading society details...</p>
        </div>
      )}

      {!loading && society && (
        <div className="sad-content">
          {/* Society Info Card */}
          <div className="sad-card">
            <div className="sad-card-header">
              <h3 className="sad-section-title">
                <HiInformationCircle size={20} /> Society Information
              </h3>
              {!editing && (
                <button className="sad-btn sad-btn-icon" onClick={() => startEditing(society)}>
                  <HiPencil size={16} /> Edit
                </button>
              )}
            </div>

            {saveError && (
              <div className="sad-alert sad-alert-error" style={{ marginBottom: 16 }}>
                <HiExclamation size={16} /> {saveError}
              </div>
            )}

            {editing ? (
              <div className="sad-form">
                <div className="sad-form-grid">
                  <div className="sad-form-group">
                    <label className="sad-label">Name</label>
                    <input
                      className="sad-input"
                      value={formData.name || ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>
                  <div className="sad-form-group">
                    <label className="sad-label">Location</label>
                    <input
                      className="sad-input"
                      value={formData.location || ''}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    />
                  </div>
                  <div className="sad-form-group">
                    <label className="sad-label">City</label>
                    <input
                      className="sad-input"
                      value={formData.city || ''}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    />
                  </div>
                  <div className="sad-form-group">
                    <label className="sad-label">State</label>
                    <input
                      className="sad-input"
                      value={formData.state || ''}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    />
                  </div>
                  <div className="sad-form-group">
                    <label className="sad-label">Pincode</label>
                    <input
                      className="sad-input"
                      value={formData.pincode || ''}
                      onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                    />
                  </div>
                  <div className="sad-form-group">
                    <label className="sad-label">Country</label>
                    <input
                      className="sad-input"
                      value={formData.country || ''}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    />
                  </div>
                  <div className="sad-form-group">
                    <label className="sad-label">Type</label>
                    <select
                      className="sad-input"
                      value={formData.type || ''}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    >
                      <option value="">Select type</option>
                      {SOCIETY_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t.charAt(0).toUpperCase() + t.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="sad-form-actions">
                  <button className="sad-btn sad-btn-primary" onClick={handleSave} disabled={saving}>
                    <HiCheck size={16} /> {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button className="sad-btn sad-btn-outline" onClick={cancelEditing} disabled={saving}>
                    <HiX size={16} /> Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="sad-info-grid">
                <InfoRow label="Name" value={society.name} />
                <InfoRow label="Location" value={society.location} />
                <InfoRow label="City" value={society.city} />
                <InfoRow label="State" value={society.state || '-'} />
                <InfoRow label="Pincode" value={society.pincode != null ? String(society.pincode) : '-'} />
                <InfoRow label="Country" value={society.country || '-'} />
                <InfoRow
                  label="Type"
                  value={society.type ? society.type.charAt(0).toUpperCase() + society.type.slice(1) : '-'}
                />
                <InfoRow label="Created" value={formatDate(society.created_at)} />
                <InfoRow label="Updated" value={formatDate(society.updated_at)} />
              </div>
            )}
          </div>

          {/* Blocks Section */}
          <div className="sad-card">
            <h3 className="sad-section-title">
              <HiOfficeBuilding size={20} /> Blocks ({blocks.length})
            </h3>
            {blocks.length === 0 ? (
              <p className="sad-empty">No blocks found for this society.</p>
            ) : (
              <div className="sad-blocks-grid">
                {blocks.map((block) => (
                  <div key={block.id} className="sad-block-card">
                    <span className="sad-block-name">{block.name}</span>
                    {block.created_at && (
                      <span className="sad-block-date">{formatDate(block.created_at)}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Settings Section */}
          <div className="sad-card" id="sad-society-settings">
            <h3 className="sad-section-title">
              <HiCog size={20} /> Society Settings
            </h3>

            {settingsError && (
              <div className="sad-alert sad-alert-error" style={{ marginBottom: 16 }}>
                <HiExclamation size={16} /> {settingsError}
              </div>
            )}

            {Object.keys(settings).length === 0 ? (
              <p className="sad-empty">No settings configured.</p>
            ) : (
              <div className="sad-table-wrapper">
                <table className="sad-table">
                  <thead>
                    <tr>
                      <th>Setting</th>
                      <th>Value</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(settings)
                      .filter(([key]) => !TAPPYTRAY_ADMIN_ONLY_SETTINGS.has(key))
                      .map(([key, value]) => (
                      <tr key={key}>
                        <td className="sad-setting-key" title={key}>
                          {SETTING_LABELS[key] || key}
                        </td>
                        <td>
                          {editingSettingKey === key ? (
                            SETTING_SELECT_OPTIONS[key] ? (
                              <select
                                className="sad-input sad-input-sm"
                                value={settingValue}
                                onChange={(e) => setSettingValue(e.target.value)}
                                autoFocus
                              >
                                {SETTING_SELECT_OPTIONS[key]!.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                className="sad-input sad-input-sm"
                                value={settingValue}
                                onChange={(e) => setSettingValue(e.target.value)}
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleUpdateSetting(key);
                                  if (e.key === 'Escape') setEditingSettingKey(null);
                                }}
                              />
                            )
                          ) : (
                            <span className="sad-setting-value">
                              {SETTING_SELECT_OPTIONS[key]?.find((o) => o.value === value)?.label || value}
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="sad-action-group">
                            {editingSettingKey === key ? (
                              <>
                                <button
                                  className="sad-btn sad-btn-xs sad-btn-primary"
                                  onClick={() => handleUpdateSetting(key)}
                                  disabled={savingSetting}
                                >
                                  <HiCheck size={14} />
                                </button>
                                <button
                                  className="sad-btn sad-btn-xs sad-btn-outline"
                                  onClick={() => setEditingSettingKey(null)}
                                  disabled={savingSetting}
                                >
                                  <HiX size={14} />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  className="sad-btn sad-btn-xs sad-btn-outline"
                                  onClick={() => {
                                    setEditingSettingKey(key);
                                    setSettingValue(value);
                                    setSettingsError('');
                                  }}
                                >
                                  <HiPencil size={14} />
                                </button>
                                <button
                                  className="sad-btn sad-btn-xs sad-btn-danger-outline"
                                  onClick={() => handleResetSetting(key)}
                                  disabled={savingSetting}
                                  title="Reset to default"
                                >
                                  <HiRefresh size={14} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Activity Logs */}
          <div className="sad-card">
            <h3 className="sad-section-title">
              <HiInformationCircle size={20} /> Recent Activity
            </h3>
            {(!data.activity_logs || data.activity_logs.length === 0) ? (
              <p className="sad-empty">No recent activity.</p>
            ) : (
              <div className="sad-activity-list">
                {data.activity_logs.map((log: AdminSocietyActivityLog, idx: number) => (
                  <div key={idx} className="sad-activity-item">
                    <div className="sad-activity-dot" />
                    <div className="sad-activity-body">
                      <div className="sad-activity-header">
                        <span className="sad-activity-title">{log.title}</span>
                        <span className="sad-activity-badge">{log.event_type}</span>
                      </div>
                      {log.detail && <p className="sad-activity-detail">{log.detail}</p>}
                      <div className="sad-activity-meta">
                        {log.actor_name && <span>{log.actor_name}</span>}
                        <span>{formatDate(log.occurred_at)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div id="sad-bill-categories" className="sad-card sad-billing-embed">
            <h3 className="sad-section-title">
              <HiDocumentText size={20} /> Bill Categories
            </h3>
            <p className="sad-embed-lead">
              Configure the bill category types available when creating or scheduling bills.
            </p>
            <SocietyAdminBillCategories embedded />
          </div>

          <div id="sad-interest-config" className="sad-card sad-billing-embed">
            <h3 className="sad-section-title">
              <HiDocumentText size={20} /> Interest configuration
            </h3>
            <p className="sad-embed-lead">
              Set overdue rules per bill category or as a society-wide default.
            </p>
            <SocietyAdminInterestConfig embedded />
          </div>
        </div>
      )}
    </div>
  );
};

const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="sad-info-row">
    <span className="sad-info-label">{label}</span>
    <span className="sad-info-value">{value}</span>
  </div>
);

export default SocietyAdminDetails;
